"use client";
import { useState, useEffect } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { getActiveClients, type ClientData } from '@/actions/clients';
import { 
  DevicePhoneMobileIcon,
  ComputerDesktopIcon,
  WifiIcon,
  BoltIcon,
  ShieldCheckIcon,
  CloudIcon,
  WrenchScrewdriverIcon,
  GlobeAltIcon,
  UsersIcon,
  CheckCircleIcon,
  PhoneIcon,
  MapPinIcon
} from '@heroicons/react/24/outline';

export default function VotingPage() {
  const [imageError, setImageError] = useState(false);
  const [clients, setClients] = useState<ClientData[]>([]);
  const [isLoadingClients, setIsLoadingClients] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Fallback clients if database is empty
  const fallbackClients = [
    'Община Ветово',
    'Община Видин', 
    'Община Враца',
    'Община Созопол',
    'Община Девня',
    'Община Провадия',
    'Община Суворово',
    'Община Белослав',
    'Община Вълчи Дол',
    'Община Аксаково'
  ];

  useEffect(() => {
    const loadClients = async () => {
      try {
        const result = await getActiveClients();
        if (result.success && result.clients.length > 0) {
          setClients(result.clients);
        }
      } catch (error) {
        console.error("Error loading clients:", error);
      } finally {
        setIsLoadingClients(false);
      }
    };

    loadClients();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      {/* Hero Section */}
      <div className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                  ЦЯЛОСТНА СИСТЕМА
                </span>
                <br />
                ЗА ЕЛЕКТРОННО ГЛАСУВАНЕ В ОБЩИНСКИ СЪВЕТ
                <br />
                <span className="text-2xl md:text-3xl text-green-600">НА ДОСТЪПНИ ЦЕНИ</span>
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                VOTING.BG е система за електронно гласуване в Общински съвет, създадена от български ИТ специалисти, 
                специално за Общинските съвети на територията на Република България.
              </p>
            </div>
            <div className="text-center">
              <div className="bg-white rounded-2xl shadow-2xl p-6">
                {!imageError ? (
                  <Image
                    src="/images/vote.png"
                    alt="Система за електронно гласуване"
                    width={500}
                    height={400}
                    className="mx-auto rounded-lg"
                    priority
                    onError={() => setImageError(true)}
                  />
                ) : (
                  <div className="flex items-center justify-center h-96 bg-gray-100 rounded-lg">
                    <DevicePhoneMobileIcon className="h-32 w-32 text-blue-600" />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Activity Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">НАШАТА ДЕЙНОСТ</h2>
            
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                  <span className="text-3xl mr-3">🗳️</span>
                  VOTING.BG / Воутинг БГ
                </h3>
                <p className="text-lg text-gray-700 mb-6">
                  Системата е изградена от:
                </p>
                <ul className="text-left text-gray-700 space-y-3">
                  <li className="flex items-start">
                    <CheckCircleIcon className="h-6 w-6 text-green-500 mr-3 mt-1 flex-shrink-0" />
                    Безжични хардуерни устройства за гласуване, разработени от нас
                  </li>
                  <li className="flex items-start">
                    <CheckCircleIcon className="h-6 w-6 text-green-500 mr-3 mt-1 flex-shrink-0" />
                    Сървър за обработка на данни
                  </li>
                  <li className="flex items-start">
                    <CheckCircleIcon className="h-6 w-6 text-green-500 mr-3 mt-1 flex-shrink-0" />
                    Дисплей за визуализиране на резултатите
                  </li>
                </ul>
              </div>
              
              <div className="space-y-6">
                <Card className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50">
                  <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                    <span className="text-2xl mr-3">📋</span>
                    Решението включва:
                  </h4>
                  <div className="space-y-3 text-gray-700">
                    <div className="flex items-start">
                      <span className="text-blue-600 mr-3">🖥️</span>
                      <span>Система за електронно гласуване и информационно обслужване работата на общинския съвет</span>
                    </div>
                    <div className="flex items-start">
                      <span className="text-blue-600 mr-3">🔒</span>
                      <span>Защитена безжична мрежа</span>
                    </div>
                    <div className="flex items-start">
                      <span className="text-blue-600 mr-3">🖧</span>
                      <span>Сървър, обслужващ системата</span>
                    </div>
                    <div className="flex items-start">
                      <span className="text-blue-600 mr-3">📊</span>
                      <span>Дисплей за визуализиране на текущо заседание, разглеждана точка и резултати от гласуването</span>
                    </div>
                    <div className="flex items-start">
                      <span className="text-blue-600 mr-3">📱</span>
                      <span>Безжични хардуерни устройства за гласуване</span>
                    </div>
                    <div className="flex items-start">
                      <span className="text-blue-600 mr-3">🔌</span>
                      <span>Комплект кабели и захранващ адаптер за зареждане</span>
                    </div>
                  </div>
                </Card>

                <Card className="p-6 bg-gradient-to-r from-green-50 to-emerald-50">
                  <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                    <span className="text-2xl mr-3">🎁</span>
                    Към системата получавате:
                  </h4>
                  <div className="space-y-3 text-gray-700">
                    <div className="flex items-start">
                      <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3 mt-1" />
                      <span>Безплатни обновления на софтуера на системата</span>
                    </div>
                    <div className="flex items-start">
                      <WrenchScrewdriverIcon className="h-5 w-5 text-green-500 mr-3 mt-1" />
                      <span>Отдалечена софтуерна поддръжка</span>
                    </div>
                    <div className="flex items-start">
                      <CloudIcon className="h-5 w-5 text-green-500 mr-3 mt-1" />
                      <span>Безплатно архивиране на базата данни на системата в защитена облачна услуга</span>
                    </div>
                    <div className="flex items-start">
                      <WrenchScrewdriverIcon className="h-5 w-5 text-green-500 mr-3 mt-1" />
                      <span>Хардуерна поддръжка на устройствата за гласуване</span>
                    </div>
                    <div className="flex items-start">
                      <CloudIcon className="h-5 w-5 text-green-500 mr-3 mt-1" />
                      <span>Безплатен уеб портал за нуждите на Общински съвет</span>
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Hardware Device Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
                <DevicePhoneMobileIcon className="h-10 w-10 text-blue-600 mr-4" />
                Хардуерно устройство за гласуване
              </h2>
              <p className="text-lg text-gray-700 mb-6">
                Устройството за гласуване е разработено от нашите инженери специално за системата за гласуване.
              </p>
              
              <div className="space-y-4">
                <h3 className="text-xl font-bold text-gray-900 flex items-center">
                  <WrenchScrewdriverIcon className="h-6 w-6 text-blue-600 mr-3" />
                  Основни характеристики:
                </h3>
                <div className="space-y-3">
                  <div className="flex items-start">
                    <span className="text-blue-600 mr-3">📏</span>
                    <span className="text-gray-700">Компактни размери, подходящи за удобство при употреба</span>
                  </div>
                  <div className="flex items-start">
                    <BoltIcon className="h-5 w-5 text-blue-600 mr-3 mt-1" />
                    <span className="text-gray-700">Презареждаема батерия за дълготрайна работа</span>
                  </div>
                  <div className="flex items-start">
                    <WifiIcon className="h-5 w-5 text-blue-600 mr-3 mt-1" />
                    <span className="text-gray-700">Вграден WiFi модул за осигуряване на надеждна безжична връзка</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-blue-600 mr-3">🔌</span>
                    <span className="text-gray-700">USB Type-C кабел за бързо и лесно зареждане</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="text-center">
              <div className="bg-white rounded-2xl shadow-2xl p-8 inline-block">
                {!imageError ? (
                  <Image
                    src="/images/project.jpg"
                    alt="Устройство за гласуване"
                    width={400}
                    height={300}
                    className="mx-auto mb-4 rounded-lg"
                    onError={() => setImageError(true)}
                  />
                ) : (
                  <div className="flex items-center justify-center h-72 bg-gray-100 rounded-lg mb-4">
                    <DevicePhoneMobileIcon className="h-24 w-24 text-blue-600" />
                  </div>
                )}
                <p className="text-gray-600 font-medium">Устройство за гласуване</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Web Portal Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="text-center">
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl shadow-2xl p-4 sm:p-6 md:p-8 inline-block w-full max-w-md">
                <div className="bg-white rounded-xl p-4 sm:p-6 mb-4 cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setIsModalOpen(true)}>
                  <Image
                    src="/images/obs.png"
                    alt="OBS Savet Portal"
                    width={300}
                    height={300}
                    className="w-full h-auto max-w-[200px] sm:max-w-[250px] md:max-w-[300px] mx-auto hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <p className="text-gray-600 font-medium text-sm sm:text-base">Уеб портал за Общински съвет</p>
                <p className="text-xs sm:text-sm text-gray-500 mt-2">obs-savet.eu</p>
              </div>
            </div>
            
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Безплатен уеб портал за Общински съвет
              </h2>
              
              <div className="space-y-4">
                <h3 className="text-xl font-bold text-gray-900 flex items-center">
                  <WrenchScrewdriverIcon className="h-6 w-6 text-blue-600 mr-3" />
                  Основни характеристики:
                </h3>
                <div className="space-y-3">
                  <div className="flex items-start">
                    <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3 mt-1" />
                    <span className="text-gray-700">Бърз и лесен за работа уеб портал</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3 mt-1" />
                    <span className="text-gray-700">Възможност за публикуване на материали за Общински съветници</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3 mt-1" />
                    <span className="text-gray-700">Архив на онлайн излъчвания на Общински съвет</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3 mt-1" />
                    <span className="text-gray-700">Интуитивен и лесен за работа администраторски интерфейс</span>
                  </div>
                </div>
                
                <div className="mt-6">
                  <a 
                    href="/#contact" 
                    
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-medium rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 hover:scale-105"
                  >
                    За повече информация попълнете контактната форма
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Clients Section */}
      <section id="clients" className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">НАШИ КЛИЕНТИ</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Доверие от общини в цяла България. От 13 до 37 Общински съветника. 
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {isLoadingClients ? (
              // Loading state
              Array.from({ length: 10 }).map((_, index) => (
                <Card key={index} className="p-6 text-center animate-pulse">
                  <div className="h-12 w-12 bg-gray-200 rounded mx-auto mb-4"></div>
                  <div className="h-4 bg-gray-200 rounded"></div>
                </Card>
              ))
            ) : clients.length > 0 ? (
              // Display database clients
              clients.map((client) => (
                <Card key={client.id} className="p-6 text-center hover:shadow-lg transition-shadow duration-300">
                  {client.logo ? (
                    <div className="h-12 w-12 mx-auto mb-4 relative">
                      <Image
                        src={client.logo}
                        alt={client.name}
                        fill
                        className="object-contain rounded"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    </div>
                  ) : (
                    <UsersIcon className="h-12 w-12 text-blue-600 mx-auto mb-4" />
                  )}
                  <h3 className="font-semibold text-gray-900 text-sm leading-tight">
                    {client.name}
                  </h3>
                  {client.url && (
                    <a
                      href={client.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-blue-600 hover:text-blue-800 mt-2 inline-block"
                    >
                      Посетете сайта
                    </a>
                  )}
                </Card>
              ))
            ) : (
              // Fallback to static clients if database is empty
              fallbackClients.map((client, index) => (
                <Card key={index} className="p-6 text-center hover:shadow-lg transition-shadow duration-300">
                  <UsersIcon className="h-12 w-12 text-blue-600 mx-auto mb-4" />
                  <h3 className="font-semibold text-gray-900">{client}</h3>
                </Card>
              ))
            )}
          </div>
        </div>
      </section>

      {/* Contact Information Section */}
      <section id="contact" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">СВЪРЖЕТЕ СЕ С НАС</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              За повече информация и оферта за вашата община
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-12 max-w-4xl mx-auto">
            <div>
              <Link href="/#contact">
                <Card className="p-8 text-center cursor-pointer hover:shadow-xl transition-all duration-300 hover:scale-105">
                  <PhoneIcon className="h-16 w-16 text-blue-600 mx-auto mb-6" />
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">КОНТАКТ</h3>
                  <div className="space-y-4">
                    <div>
                      <p className="font-semibold text-gray-900 text-lg">Ивайло Начев</p>
                      <div className="text-2xl font-bold text-blue-600">
                        0898773983
                      </div>
                    </div>
                    <div className="pt-4">
                      <p className="text-gray-700 font-medium">Воутинг БГ ООД</p>
                    </div>
                    <div className="pt-2">
                      <p className="text-sm text-blue-600 font-medium">Натиснете за контактна форма</p>
                    </div>
                  </div>
                </Card>
              </Link>
            </div>
            
            <Card className="p-8 text-center hover:shadow-xl transition-all duration-300">
              <GlobeAltIcon className="h-16 w-16 text-green-600 mx-auto mb-6" />
              <h3 className="text-2xl font-bold text-gray-900 mb-4">УЕБ ПОРТАЛ</h3>
              <div className="space-y-4">
                <p className="text-gray-700 mb-4">
                  Разгледайте нашия уеб портал за общински съвети
                </p>
                <div className="space-y-2">
                  {/* <a 
                    href="https://obs-savet.eu" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white font-medium rounded-lg hover:from-green-700 hover:to-emerald-700 transition-all duration-300 hover:scale-105 shadow-lg"
                  >
                    Посетете OBS-SAVET.EU
                  </a> */}
                  <div className="mt-2">
                    <Link href="/#contact" className="text-sm text-blue-600 font-medium hover:text-blue-800 transition-colors">
                     За информация натиснете за контактна форма
                    </Link>
                  </div>
                </div>
              </div>
            </Card>
          </div>
          
          <div className="text-center mt-12">
            <div className="inline-flex items-center space-x-4 bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg">
              <span className="text-2xl">📘</span>
              <div>
                <h4 className="font-bold text-gray-900 mb-2">НАМЕРЕТЕ НИ В СОЦИАЛНИТЕ МРЕЖИ</h4>
                <a 
                  href="https://www.facebook.com/profile.php?id=61571164910771" 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  <Button variant="outline" className="flex items-center space-x-2 hover:bg-blue-50 transition-colors">
                    <span>Facebook</span>
                  </Button>
                </a>
              </div>
            </div>
          </div>
          
          <div className="text-center mt-8">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg max-w-2xl mx-auto">
              <h4 className="font-bold text-gray-900 mb-2">СИСТЕМА ЗА ЕЛЕКТРОННО ГЛАСУВАНЕ В ОБЩИНСКИ СЪВЕТ</h4>
              <p className="text-sm text-gray-600">© 2024 Copyright: voting.bg © всички права запазени!</p>
            </div>
          </div>
        </div>
      </section>

      {/* Modal for OBS Image */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4" onClick={() => setIsModalOpen(false)}>
          <div className="relative max-w-4xl max-h-[90vh] bg-white rounded-lg overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setIsModalOpen(false)}
              className="absolute top-4 right-4 z-10 bg-white bg-opacity-80 hover:bg-opacity-100 rounded-full p-2 transition-all duration-200"
            >
              <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <div className="p-6">
              <Image
                src="/images/obs.png"
                alt="OBS Savet Portal - Full View"
                width={800}
                height={600}
                className="w-full h-auto object-contain"
              />
              <div className="mt-4 text-center">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Уеб портал за Общински съвет</h3>
                <p className="text-gray-600">obs-savet.eu</p>
                <a 
                  href="https://obs-savet.eu" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-block mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Посетете портала
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
